import { FC, useMemo } from 'react';

import { DeliveryNoteResponse, DocumentOrderLineData, OrderDocumentType } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { DeliveryNoteContent } from './delivery-note-content';

interface Props {
   deliveryNoteDocs: Array<DeliveryNoteResponse>;
   onRequestDeliveryNote: (deliveryNote: DeliveryNoteResponse) => void;
   onDownload: (document: DeliveryNoteResponse, orderLineData: DocumentOrderLineData, type: OrderDocumentType) => void;
   onViewDocument: (document: DeliveryNoteResponse, orderLineData: DocumentOrderLineData) => void;
}

const DeliveryDocuments: FC<Props> = ({ deliveryNoteDocs, onDownload, onViewDocument, onRequestDeliveryNote }) => {
   const translate = useTranslations();

   const [downloadableDocuments, requestableDocuments] = useMemo(
      () => [deliveryNoteDocs.filter((doc) => doc.id), deliveryNoteDocs.filter((doc) => !doc.id)],
      [deliveryNoteDocs]
   );

   const showDivider = downloadableDocuments.length > 0 && requestableDocuments.length > 0;

   const renderDocuments = (documents: Array<DeliveryNoteResponse>, showDownloadBtn: boolean) =>
      documents.map((deliveryNoteDoc, index) => (
         <Paper direction="row" key={deliveryNoteDoc.id || `doc-${index}`} width="100%">
            <Box justify="between" items="start" width="100%" direction="row">
               <DeliveryNoteContent
                  data={deliveryNoteDoc}
                  onDownload={onDownload}
                  onViewDocument={onViewDocument}
                  showDownloadBtn={showDownloadBtn}
                  onRequestDeliveryNote={onRequestDeliveryNote}
               />
            </Box>
         </Paper>
      ));

   return (
      <Box direction="column" gap="sm">
         {deliveryNoteDocs.length > 0 ? (
            <>
               {renderDocuments(downloadableDocuments, true)}
               {showDivider && <Divider />}
               {renderDocuments(requestableDocuments, false)}
            </>
         ) : (
            <Typography variant="body" component="span">
               {translate('shipment.documents.no-documents-available')}
            </Typography>
         )}
      </Box>
   );
};

export default DeliveryDocuments;
